/** Core service catalogue (/services/$slug) — also the source for city × service page titles and FAQs. */
export type Service = {
  slug: string;
  title: string;
  tagline: string;
  description: string;
  deliverables: string[];
  outcomes: string[];
  faqs: { q: string; a: string }[];
};

export const SERVICES: Service[] = [
  {
    slug: "seo",
    title: "Search Engine Optimization",
    tagline: "Rank for the searches your buyers actually make.",
    description:
      "End-to-end SEO programs combining keyword strategy, on-page optimization, technical fixes, and authority building — engineered to compound traffic month over month.",
    deliverables: [
      "Buyer-intent keyword research & topical map",
      "On-page optimization for every commercial page",
      "Internal linking & content cluster architecture",
      "Monthly performance dashboard with CTR, position, conversions",
    ],
    outcomes: ["More visibility for buyer-intent searches", "Rankings and clicks tracked against a baseline", "Leads from search measured monthly"],
    faqs: [
      { q: "How long does SEO take to show results?", a: "There is no fixed timeline. It depends on your site's starting point, competition and how much work ships. You get a baseline in week one and a monthly report comparing rankings, clicks and leads against it — no ranking date is promised." },
      { q: "What's the difference between on-page and off-page SEO?", a: "On-page is everything you control — content, structure, internal links, schema. Off-page is signals from outside your site, mainly backlinks, brand mentions, and digital PR. You need both." },
      { q: "Is SEO worth it in 2026 with AI search?", a: "Yes — and arguably more than ever. AI Overviews and answer engines pull from the same indexed, well-structured content that ranks in classic search. Strong SEO is the foundation AEO is built on." },
      { q: "Can I do SEO myself?", a: "For a small local site — absolutely, with the right framework. For competitive niches or ecommerce, an experienced consultant helps you avoid the expensive mistakes and decide what to fix first." },
    ],
  },
  {
    slug: "local-seo",
    title: "Local SEO (Rangpur & Bangladesh)",
    tagline: "Own the map pack in your city.",
    description:
      "Win Google Business Profile rankings, dominate “near me” searches, and turn nearby searchers into walk-ins and phone calls.",
    deliverables: [
      "Google Business Profile optimization & weekly posts",
      "NAP citation cleanup across 50+ directories",
      "Local landing pages targeted by neighborhood",
      "Review generation system (ethical, TOS-safe)",
    ],
    outcomes: ["A complete, accurate Google Business Profile", "Stronger map-pack visibility in your area", "Calls and direction requests tracked monthly"],
    faqs: [
      { q: "How do I rank #1 in Google Maps?", a: "Three pillars: a fully-optimized Google Business Profile (categories, services, photos, posts), consistent NAP citations across the web, and a steady stream of authentic reviews. Proximity to the searcher is the fourth lever Google controls." },
      { q: "How long does local SEO take?", a: "It varies. Map-pack movement depends on your Google Business Profile, reviews, location and how active competitors are — busier cities like Dhaka and Chattogram are usually slower. Progress is tracked monthly against a starting baseline." },
      { q: "Do I need a website for local SEO?", a: "Technically no — a strong GBP can rank by itself. But a fast, schema-rich website gives Google more to work with and lets you compete in the organic results below the map pack too." },
      { q: "What is NAP consistency and why does it matter?", a: "NAP = Name, Address, Phone. When these match exactly across your GBP, website, Facebook, directories, and citations, it is easier for Google to trust that your business details are accurate." },
    ],
  },
  {
    slug: "technical-seo-audit",
    title: "Technical SEO Audit",
    tagline: "Find every silent ranking killer hiding in your stack.",
    description:
      "A 200-point audit covering crawlability, Core Web Vitals, indexation, schema, JavaScript rendering, and international SEO — delivered as a prioritized fix-it roadmap.",
    deliverables: [
      "Full crawl + log file analysis",
      "Core Web Vitals & page-experience report",
      "Schema, hreflang, canonical & indexation review",
      "Engineer-ready fix tickets in priority order",
    ],
    outcomes: ["Crawl and indexing issues found and fixed", "Faster indexation of new content", "Cleaner crawl budget"],
    faqs: [
      { q: "What is a technical SEO audit?", a: "A systematic review of how search engines crawl, render, and index your site. It surfaces bugs that quietly suppress rankings — broken canonicals, slow LCP, JS rendering issues, schema errors, orphan pages, and more." },
      { q: "How often should I run a technical SEO audit?", a: "A full audit annually, a lightweight crawl quarterly, and always after a redesign, replatform, or migration." },
      { q: "Are Core Web Vitals a ranking factor?", a: "Yes — they're part of Google's page-experience signals. They rarely outweigh great content, but on a competitive SERP they can be the tiebreaker between you and a competitor." },
      { q: "What tools do you use for technical audits?", a: "Screaming Frog, Sitebulb, Ahrefs Site Audit, Google Search Console, PageSpeed Insights, the Schema validator, and a log-file analyzer for big sites." },
    ],
  },
  {
    slug: "digital-marketing",
    title: "Digital Marketing Strategy",
    tagline: "An organic-first growth plan that actually ships.",
    description:
      "One strategy that ties SEO, content, email, and social together — with a 90-day execution plan you can hand to your team or have me run end-to-end.",
    deliverables: [
      "Audience, offer, and channel-fit audit",
      "90-day execution roadmap with KPIs",
      "Content calendar & distribution system",
      "Monthly strategy + reporting calls",
    ],
    outcomes: ["Acquisition cost tracked per channel", "An organic channel that builds over time", "Clear, defensible growth thesis"],
    faqs: [
      { q: "Do you do paid ads too?", a: "I plan and advise on paid (Google, Meta, LinkedIn) as part of an integrated strategy, but my hands-on execution is organic-first. For paid execution I partner with specialists I trust." },
      { q: "What's the difference between digital marketing and SEO?", a: "SEO is one channel inside digital marketing. Digital marketing covers SEO + content + email + social + paid + CRO. My strategy work makes sure they reinforce each other instead of competing for budget." },
      { q: "How do you measure success?", a: "Pipeline, not vanity metrics. We define 2–3 north-star KPIs at the start (e.g. SQLs, organic revenue, branded search volume) and report against them monthly." },
    ],
  },
  {
    slug: "aeo-geo",
    title: "AEO & GEO (AI Search)",
    tagline: "Get cited by ChatGPT, Perplexity, Gemini & Google AI Overviews.",
    description:
      "Answer Engine and Generative Engine Optimization for brands who want to be the source AI assistants quote — with structured content, entity SEO, and citation-worthy assets.",
    deliverables: [
      "Entity & brand knowledge graph build-out",
      "Citation-friendly content & schema markup",
      "AI visibility tracking across ChatGPT, Perplexity, Gemini",
      "llms.txt + AI crawler access policy",
    ],
    outcomes: ["Citation-ready content and schema", "Clearer brand and entity signals", "AI answer visibility tracked on priority prompts"],
    faqs: [
      { q: "What is Answer Engine Optimization (AEO)?", a: "AEO is the practice of optimizing content and entities so that AI answer engines — ChatGPT, Perplexity, Gemini, Google AI Overviews — quote and cite your brand in their responses." },
      { q: "How is AEO different from SEO?", a: "SEO targets the 10 blue links. AEO targets the synthesized answer above (or instead of) them. AEO leans more heavily on structured content, entity associations, schema, and citation-worthy assets." },
      { q: "How do I get cited by ChatGPT or Perplexity?", a: "Build clear entity associations, publish unique data and primary research, use FAQ + HowTo + Article schema, earn mentions on sites those models trust, and structure answers as short, quotable paragraphs." },
      { q: "Do I need an llms.txt file?", a: "It's optional but signals intent. An llms.txt summarizes your site for LLM crawlers, and combined with a sensible robots policy it lets you control which AI bots get full access." },
    ],
  },
  {
    slug: "ecommerce-seo",
    title: "Ecommerce SEO",
    tagline: "Turn category and product pages into compounding revenue.",
    description:
      "Shopify, WooCommerce and headless ecommerce SEO — built around buyer-intent collections, faceted navigation, and product schema that wins rich results.",
    deliverables: [
      "Category & collection keyword mapping",
      "Faceted nav, canonical & indexation strategy",
      "Product, review & FAQ schema",
      "Merchandising & internal-link revenue plays",
    ],
    outcomes: ["Non-brand organic revenue tracked separately", "Product rich-result eligibility", "Collections mapped to buyer searches"],
    faqs: [
      { q: "Is Shopify good for SEO?", a: "Yes — Shopify's defaults are solid. The platform's limitations (URL structure, blog, robots.txt) are workable. The bigger lever is your collection strategy, content, and schema, which are platform-agnostic." },
      { q: "Should I write product descriptions for SEO or for buyers?", a: "Both. Lead with the buyer (benefits, specs, social proof) and bake in the keywords naturally. Manufacturer-supplied descriptions duplicated across the web will not rank." },
      { q: "How do I handle faceted navigation without tanking my crawl budget?", a: "Decide which filter combinations have search demand and let those be indexable; canonicalize or noindex the rest. Done well, faceted nav becomes a long-tail revenue engine instead of a crawl trap." },
    ],
  },
  {
    slug: "link-building",
    title: "Authority & Link Building",
    tagline: "Earn the kind of links that actually move rankings.",
    description:
      "Editorial link acquisition through digital PR, data studies and partnerships — no PBNs, no spam, no risk to your domain.",
    deliverables: [
      "Link gap & competitor backlink analysis",
      "Digital PR campaigns & data-led outreach",
      "HARO, podcast & guest-feature placements",
      "Monthly link velocity & DR reporting",
    ],
    outcomes: ["Relevant editorial links, documented", "Stronger topical authority", "Authority gaps against competitors reviewed quarterly"],
    faqs: [
      { q: "Are backlinks still important in 2026?", a: "Yes — they remain one of Google's strongest ranking signals, and they help LLMs decide which sources to trust. Quality and topical relevance matter far more than raw quantity." },
      { q: "Do you buy links or use PBNs?", a: "Never. Every link I help clients earn is editorial — through digital PR, original data, podcast features, and partnerships. Paid/PBN links are a short-term high and a long-term penalty." },
      { q: "How many backlinks do I need to rank?", a: "There's no fixed number. The honest answer: enough to match or exceed the topical authority of the pages currently ranking on page one for your target keyword. A link gap analysis tells you exactly." },
    ],
  },
  {
    slug: "content-strategy",
    title: "Content Strategy & Production",
    tagline: "SEO-led content your buyers actually finish reading.",
    description:
      "Topical maps, briefs, and editor-grade production for blogs, hubs, and programmatic pages — ranked by search intent and built to convert.",
    deliverables: [
      "Topical map & content cluster plan",
      "SERP-driven briefs & editorial guidelines",
      "Long-form article & landing page production",
      "Refresh & decay-recovery program",
    ],
    outcomes: ["Content mapped to real search demand", "Engagement measured per article", "Assisted conversions tracked"],
    faqs: [
      { q: "How often should I publish blog content?", a: "Quality and topical depth beat raw frequency. For most B2B brands, 4–8 senior-edited articles per month outperform 30 thin posts. Build clusters, not calendars." },
      { q: "Should I use AI to write SEO content?", a: "AI is fantastic for outlines, research, and first drafts. But pure AI content rarely ranks long-term — it lacks experience, original data, and a human point of view. Use AI to go faster, not to skip the thinking." },
      { q: "What is a content cluster?", a: "A pillar page targeting a broad term, supported by 8–20 cluster articles answering specific sub-questions. Internal links flow up to the pillar. It's the most reliable way to build topical authority on a topic." },
    ],
  },
];
